import { emit } from '../events';
import { inGesture } from '../input/activeAdapter';
import { isUnlocked } from './audioUnlock';
import { useAudioStatus } from './audioStatusStore';
import { offerTapToSpeak, useTapToSpeak } from './useTapToSpeak';
import {
  ProviderUnavailable,
  type ProviderId,
  type SpeakOpts,
  type SpeakResult,
  type TtsContext,
  type TtsProvider,
} from './TtsProvider';
import type { UtteranceSource } from '../types';

/**
 * Resolves the provider chain and owns the abort signal.
 *
 * Fixed order: `cachedAudio → azureTts → piperLocal → webSpeech → visualOnly`. The chain is built
 * from **registered** providers, so Phase 1 shipping without Piper needs no special case and Phase
 * 2 adding it needs no edit here (flagged as C9 — the contract states the order as a literal
 * five-element list, which would have meant a hole).
 *
 * Two behaviours worth stating because they are easy to get subtly wrong:
 *
 *  - **A new utterance cancels the one in flight.** Given tremor and one-tap speaking, a second
 *    tap almost always means "not that, this" — and two voices talking over each other is worse
 *    than either alone.
 *  - **`UtteranceSpoken` is emitted here**, not by the composer, because only this knows which
 *    provider actually succeeded (C19). It carries the text for `LearningService`;
 *    `DiagnosticsService` is responsible for stripping it before anything is persisted (NFR7).
 */

const CHAIN_ORDER: readonly ProviderId[] = [
  'cachedAudio',
  'azureTts',
  'piperLocal',
  'webSpeech',
  'visualOnly',
];

export interface SpeakRequest {
  text: string;
  source: UtteranceSource;
  isPhrase?: boolean;
  voiceId?: string;
}

export class SpeechService {
  private readonly providers = new Map<ProviderId, TtsProvider>();
  private current: AbortController | null = null;
  private lastResult: SpeakResult | null = null;

  register(provider: TtsProvider): void {
    this.providers.set(provider.id, provider);
  }

  /** Ordered, registered providers. */
  chain(): TtsProvider[] {
    return CHAIN_ORDER.map((id) => this.providers.get(id)).filter(
      (provider): provider is TtsProvider => provider !== undefined,
    );
  }

  private context(): TtsContext {
    return {
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      inGesture: inGesture(),
      audioUnlocked: isUnlocked(),
    };
  }

  /** Global STOP. Must silence the device within ~100 ms (non-negotiable §3.3). */
  stop(): void {
    this.current?.abort();
    this.current = null;
  }

  isSpeaking(): boolean {
    return this.current !== null;
  }

  lastProvider(): SpeakResult | null {
    return this.lastResult;
  }

  async speak(request: SpeakRequest): Promise<SpeakResult | null> {
    const text = request.text.replace(/\s+/gu, ' ').trim();
    if (text === '') return null;

    /**
     * When the person asked, for `DIA-01`'s `msToAudio`.
     *
     * Taken here rather than after `requireRunning()`, because reviving the context is part of what
     * the person waited through — and on the C80 path it is *all* of what they waited through.
     */
    const askedAtMs = performance.now();

    // A new utterance makes any parked offer stale — the person has moved on, and a button offering to
    // say the previous sentence would be a trap.
    useTapToSpeak.getState().clear();

    // Supersede rather than queue.
    this.stop();
    const controller = new AbortController();
    this.current = controller;

    const opts: SpeakOpts = {
      signal: controller.signal,
      isPhrase: request.isPhrase ?? false,
      source: request.source,
      ...(request.voiceId ? { voiceId: request.voiceId } : {}),
    };

    /**
     * Give a suspended context one chance before the chain decides it does not exist (C71).
     *
     * `isUnlocked()` is false for a context iOS suspended while the app was in the background, and every
     * provider that plays through it reports itself unavailable on that basis — so without this the chain
     * routes *around* the suspension silently: to `speechSynthesis` in a different voice, or to `visualOnly`,
     * which replaces the keyboard. `requireRunning()` resumes it where a resume can work and raises
     * `Ton aktivieren – antippen` where one cannot, which is the honest response and the only one that gives
     * the person a way back.
     *
     * **After the controller is registered, not before.** This is the one `await` between the caller and the
     * abort signal, and putting it earlier means a STOP arriving in that window has nothing to abort — which
     * broke `INV-03`'s 100 ms budget the first time this was written, and the tests said so.
     */
    await useAudioStatus.getState().requireRunning();
    if (controller.signal.aborted) return null;

    const ctx = this.context();

    try {
      for (const provider of this.chain()) {
        if (controller.signal.aborted) return null;

        if (!provider.isAvailable(ctx)) {
          // Not a failure — an honest "not me for this activation". Recorded so a chain that
          // always falls through can be seen in diagnostics.
          emit({ type: 'SpeechFailed', providerId: provider.id, reason: 'unavailable' });
          continue;
        }

        try {
          const result = await provider.speak(text, opts);
          this.lastResult = result;

          // Reaching `visualOnly` in a timed mode means the platform voice was skipped *because* the
          // activation was not a gesture — and a tap would change that answer. So offer one (3.2). In
          // direct touch the same fallthrough means no voice exists at all, and a button that cannot
          // help would be worse than the partner view because it would look like a fix.
          offerTapToSpeak(text, result.providerId);

          if (result.silent) {
            // Playback claimed to run but the clock did not move. Suspect Control Center mute;
            // tell the person rather than letting them believe they were heard.
            useAudioStatus.getState().reportSilentPlayback(true);
          } else {
            useAudioStatus.getState().reportSilentPlayback(false);
          }

          emit({
            type: 'UtteranceSpoken',
            text,
            source: request.source,
            providerId: result.providerId,
            // Omitted rather than zeroed when the provider cannot say when sound began. A `visualOnly`
            // fall-through recorded as 0 ms would read as the fastest utterance the device ever made.
            ...(result.audioAtMs === undefined
              ? {}
              : { msToAudio: Math.round(result.audioAtMs - askedAtMs) }),
          });
          return result;
        } catch (err) {
          if (controller.signal.aborted) return null;
          const reason =
            err instanceof ProviderUnavailable
              ? err.message
              : err instanceof Error
                ? err.message
                : String(err);
          emit({ type: 'SpeechFailed', providerId: provider.id, reason });
          // Fall through to the next provider. Never rethrow: the person asked to speak, and an
          // exception reaching the surface would mean nothing happens at all.
        }
      }

      // Only reachable if visualOnly was never registered, which is a wiring bug.
      console.error('[speech] chain exhausted with no provider — visualOnly not registered?');
      return null;
    } finally {
      if (this.current === controller) this.current = null;
    }
  }
}

/** One instance for the app. Surfaces call this; they never touch a provider (the layering rule, CON-10). */
export const speechService = new SpeechService();
